import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { updateUserInfo } from "../app/slices/userSlice";
import { get } from "../services/api";

/**
 * Restore the signed-in user from the auth cookie at app boot.
 *
 * Historically only the NotLoggedIn route wrapper called the
 * /users/bootstrap endpoint, which meant routes outside that wrapper
 * (notably the marketing landing once it was promoted into the
 * always-public boundary) couldn't tell whether the visitor was
 * authenticated until they clicked through to a NotLoggedIn page. The
 * marketing surface needs auth state too — to swap "Start free" for
 * "Open workspace", to surface an account menu, and to preserve
 * conversion attribution across the marketing → app boundary.
 *
 * This hook idempotently restores the session: if Redux already has
 * a user, the underlying query is disabled. Errors are swallowed
 * (skipAuthRedirect so the API helper doesn't redirect to login on
 * 401, retry: false so we don't hammer /bootstrap on flaky networks).
 */
export function useSessionBootstrap() {
  const dispatch = useDispatch();
  const userInfo = useSelector((state) => state?.user?.userinfo);

  const { data: restoredSession } = useQuery({
    queryKey: ["session-bootstrap"],
    queryFn: async () => {
      const response = await get("/api/v1/users/bootstrap", {
        skipAuthRedirect: true,
      });
      return response?.data || response || {};
    },
    enabled: !userInfo,
    staleTime: 5 * 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (userInfo || !restoredSession?.user) return;
    dispatch(updateUserInfo(restoredSession.user));
  }, [dispatch, restoredSession?.user, userInfo]);
}
